import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "wiz-front-shared/components/button/Button";
import { Input, InputInfoMsg } from "components/common/input/Input";
import { SelectCountryBox } from "components/common/selectCountryBox/SelectCountryBox";
import {
    getCountryCallingCode,
    getCountries,
    isValidPhoneNumber,
    type Country,
} from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";
import { toNationalNumber } from "utils/phoneNumber";
import "./Identity.css";

export interface IdentityGlobalPhoneResult {
    phone: string;          // 하이픈 제외 숫자
    ctnCountryCd: string;   // "+82" 등
    country: Country;       // ISO2 ("US", "KR" 등)
}

interface Props {
    /** 초기 국가 (없으면 "US") */
    initialCountry?: Country;
    /** 초기 전화번호 */
    initialPhone?: string;

    /** 보호자 ctn (+82) — 같은 번호 입력 방지 (SignUp 14세미만 본인 인증) */
    parentCtn?: string;
    /** 보호자 ctn 의 국가코드 (기본 +82 — 기존 다날 보호자) */
    parentCtnCountryCd?: string;

    /** 상단 타이틀 i18n key (기본 SignUp 용) — Recovery 등에서 override */
    titleI18nKey?: string;
    /** 상단 안내 i18n key (기본 SignUp 용) — Recovery 등에서 override */
    infoI18nKey?: string;

    /** 다음 버튼 콜백 — 최종 값을 직접 전달 (race-free) */
    onNext: (result: IdentityGlobalPhoneResult) => void;
}

/**
 * 본인인증 - 국가코드 + 전화번호 (해외 / SMS OTP)
 *
 * State 는 form 내부에서만 관리. 부모는 `onNext(result)` 로 최종 값 수령.
 */
export const IdentityGlobalPhoneForm = (props: Props) => {
    const { t } = useTranslation();

    const {
        initialCountry,
        initialPhone,
        parentCtn,
        parentCtnCountryCd = "+82",
        titleI18nKey = "signUp.step4.title",
        infoI18nKey = "signUp.step4.info",
        onNext,
    } = props;

    const [country, setCountry] = useState<Country>(initialCountry || "US");
    const countryOptions = getCountries().map(code => ({
        value: code,
        label: en[code] || code,
    }));

    const phoneRef = useRef<HTMLInputElement>(null);
    const [phoneValue, setPhoneValue] = useState(initialPhone || "");
    const [phoneInfoMsg, setPhoneInfoMsg] = useState<InputInfoMsg>();
    const [isPhoneValid, setIsPhoneValid] = useState(false);

    /**
     * 서버 저장/발송용 ctn 정규화 — 전 국가 공통으로 libphonenumber 의 national number 를 쓴다.
     *   trunk prefix(선행 0) 를 떼야 하는지는 국가마다 다르므로(한국·일본은 표기용이라 제거,
     *   이탈리아·코트디부아르는 번호의 일부라 유지) 판단을 라이브러리에 맡긴다.
     *   다날이 요구하는 국내 표기는 요청을 만드는 시점에 toDanalCtn 으로 변환한다.
     */
    const buildCtn = (raw: string, c: Country): string => toNationalNumber(raw, c);

    // 진입 시 자동 포커스 — Input 컴포넌트의 onFocus 가 keyboard-aware 스크롤 처리.
    useEffect(() => {
        const timer = setTimeout(() => phoneRef.current?.focus(), 300);
        return () => clearTimeout(timer);
    }, []);

    // 전화번호 검증
    useEffect(() => {
        if (phoneValue.trim().length === 0) {
            setIsPhoneValid(false);
            setPhoneInfoMsg(undefined);
            return;
        }

        // parentCtn 과 동일하면 invalid (SignUp 14세미만 본인 인증)
        // 정규화된 ctn 끼리 비교 — 보호자 ctn 도 동일 정규화 결과(예: "1012345678")로 저장돼 있음.
        if (parentCtn) {
            const currentCountryCode = `+${getCountryCallingCode(country)}`;
            const currentPhone = buildCtn(phoneValue, country);
            if (currentCountryCode === parentCtnCountryCd && currentPhone === parentCtn) {
                setIsPhoneValid(false);
                setPhoneInfoMsg({ msg: t("signUp.step4.sameAsParentPhone"), type: "error" });
                return;
            }
        }

        // libphonenumber 로 국가별 형식/자릿수 검증 (기존 length>=7 단순 검증 대체)
        const isValid = isValidPhoneNumber(phoneValue, country);
        setIsPhoneValid(isValid);
        setPhoneInfoMsg(!isValid ? { msg: t("signUp.phoneErrorMsg"), type: "error" } : undefined);
    }, [phoneValue, country, parentCtn, parentCtnCountryCd, t]);

    const handleNext = () => {
        if (!isPhoneValid) return;
        // 저장/발송용으로 정규화된 ctn (전 국가 공통 national number)
        const phone = buildCtn(phoneValue, country);
        const ctnCountryCd = `+${getCountryCallingCode(country)}`;
        onNext({ phone, ctnCountryCd, country });
    };

    return (
        <div className="identity-form">
            <span className="identity-title">{t(titleI18nKey)}</span>
            <span className="identity-info">{t(infoI18nKey)}</span>

            <div className="phone-input-wrapper">
                <label className="phone-input-label">
                    {t("signUp.step4.phoneNumber")}
                    <span className="required">{t("required")}</span>
                </label>
                <div className="phone-input-row">
                    <SelectCountryBox
                        value={country}
                        onChange={setCountry}
                        options={countryOptions}
                    />
                    <Input
                        id="identity-global-phone"
                        ref={phoneRef}
                        inputVal={phoneValue}
                        setInputVal={setPhoneValue}
                        onChange={(e) => setPhoneValue(e.target.value.replace(/[^0-9]/g, ""))}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder={t("signUp.step4.phoneNumberPlaceholder")}
                        showClearButton
                        notShowInfo
                        containerStyle={{ flex: 1, padding: 0 }}
                    />
                </div>
                {phoneInfoMsg && <span className="phone-input-error">{phoneInfoMsg.msg}</span>}
            </div>

            <div className="identity-bottom-btn">
                <Button
                    text={!isPhoneValid ? t("signUp.btnNext") : t("signUp.btnSMS")}
                    onPress={handleNext}
                    disabled={!isPhoneValid}
                />
            </div>
        </div>
    );
};
